import React, { useImperativeHandle, useMemo, useState } from 'react';
import { centerCrop, makeAspectCrop } from 'react-image-crop';
import { MultiPhotoSelector } from '@shared/components/MultiPhotoSelector';
import { useDisclosure } from '@withjoy/joykit';
import { PhotoUploader } from '@apps/admin/components/PhotoUploader/PhotoUploader.lazy';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { AddMediaArgs } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { PICKER_TYPES } from '@apps/admin/components/PhotoPickerDialog/PhotoPickerDialog.types';
import { useGetEventMediaQuery } from '@apps/admin/components/PhotoUploader/PhotoUploader.graphql.generated';
import { PhotoXPosition, PhotoYPosition } from '@graphql/generated-base';
import { useEventInfo } from '@shared/utils/eventInfo';
import type { MediaPhoto, PhotoSelectorHandle, PhotoSelectorProps } from './PhotoSelector.types';
import type { MediaPhotoWithCropAndAlignmentInfo } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/HeaderPhotoCustomization.types';
import { isActiveMediaPhoto } from '@shared/utils/eventPhoto/isPhotoTombstoned';

/**
 * `ref` exposes `openPicker()` so a control outside this component can open the photo picker —
 * the stock-photo overlay's "Add photo" sits on the preview image, in the other column. Optional:
 * every existing consumer passes no ref and behaves exactly as before.
 */
export const PhotoSelector = React.forwardRef<PhotoSelectorHandle, PhotoSelectorProps>((props, ref) => {
  const { min: minPhotos, max: maxPhotos, aspectRatio, onChange, value: selectedPhotoInfo, useCase = { area: 'admin', subArea: 'message' }, onDialogOpen, onDialogClose } = props;

  const [photoIdToSwap, setPhotoIdToSwap] = useState<string | null>(null);

  const { isOpen: isOpenPickerDialog, onOpen: onOpenPickerDialog, onClose: onClosePickerDialog } = useDisclosure();

  const { eventInfo, eventHandle } = useEventInfo();

  const { data: eventMedia, loading: isLoadingEventMedia } = useGetEventMediaQuery({
    variables: {
      eventHandle: eventHandle || '',
    },
    skip: !eventHandle,
    batchMode: 'fast',
  });

  const media = useMemo(() => {
    const mediaPhotos = (eventMedia?.eventByName?.media?.filter(m => isActiveMediaPhoto(m)) || []) as MediaPhoto[];
    return mediaPhotos?.sort((a, b) => {
      return b.createdAt.milliseconds - a.createdAt.milliseconds;
    });
  }, [eventMedia]);

  const onSavePhoto = useEventCallback((photos: MediaPhotoWithCropAndAlignmentInfo[]) => {
    onChange(photos);
  });

  const turnOffSwapMode = useEventCallback(() => {
    setPhotoIdToSwap(null);
  });

  const handleOpenPickerDialog = useEventCallback(() => {
    onDialogOpen?.();
    onOpenPickerDialog();
  });

  useImperativeHandle(ref, () => ({ openPicker: handleOpenPickerDialog }), [handleOpenPickerDialog]);

  const handleClosePickerDialog = useEventCallback(() => {
    turnOffSwapMode();
    onClosePickerDialog();
    onDialogClose?.();
  });

  const handlePhotoSelect = useEventCallback(({ selectedItemsId, cb }: AddMediaArgs) => {
    const selectedItems = selectedItemsId
      .map(id => {
        const alreadySelectedPhoto = selectedPhotoInfo.find(photo => photo.id === id);
        const mediaPhoto = media.find(photo => photo.id === id);

        if (alreadySelectedPhoto) {
          return alreadySelectedPhoto;
        } else if (mediaPhoto) {
          return {
            id: mediaPhoto.id,
            photoEntityId: mediaPhoto.photo.id,
            height: mediaPhoto.photo.height,
            width: mediaPhoto.photo.width,
            url: mediaPhoto.photo.url,
            cropInfo:
              aspectRatio && typeof aspectRatio === 'number'
                ? centerCrop(
                    makeAspectCrop(
                      {
                        unit: '%',
                        width: 90,
                      },
                      aspectRatio,
                      mediaPhoto.photo.width,
                      mediaPhoto.photo.height,
                    ),
                    mediaPhoto.photo.width,
                    mediaPhoto.photo.height,
                  )
                : undefined,
            alignment:
              aspectRatio === 'locked'
                ? {
                    alignX: PhotoXPosition.center,
                    alignY: PhotoYPosition.center,
                  }
                : undefined,
          } as MediaPhotoWithCropAndAlignmentInfo;
        }
        return null;
      })
      .filter((photo): photo is MediaPhotoWithCropAndAlignmentInfo => photo !== null);

    if (photoIdToSwap) {
      const index = selectedPhotoInfo.findIndex(photoInfo => photoInfo.id === photoIdToSwap);
      if (index !== -1) {
        const newPhotos = [...selectedPhotoInfo];
        newPhotos.splice(index, 1, ...selectedItems);
        onSavePhoto(newPhotos);
      }
      turnOffSwapMode();
    } else {
      onSavePhoto(selectedItems);
    }
    cb();
    handleClosePickerDialog();
  });

  const handleOnChange = useEventCallback((updatedItems: ReadonlyArray<MediaPhotoWithCropAndAlignmentInfo>) => {
    onSavePhoto([...updatedItems]);
  });

  const handlePhotoDelete = useEventCallback((photoId: string) => {
    const dataToUpdate = selectedPhotoInfo.filter(photoInfo => photoInfo.id !== photoId);
    onSavePhoto(dataToUpdate);
  });

  const handlePhotoSwap = useEventCallback((photoId: string) => {
    setPhotoIdToSwap(photoId);
    handleOpenPickerDialog();
  });

  const handleDragEnd = useEventCallback((photoIds: ReadonlyArray<string>) => {
    const selectedItems = photoIds
      .map(id => selectedPhotoInfo.find(photo => photo.id === id) || null)
      .filter((photo): photo is MediaPhotoWithCropAndAlignmentInfo => photo !== null);
    onSavePhoto(selectedItems);
  });

  const selectedItemsId = useMemo(() => selectedPhotoInfo.map(photo => photo.id), [selectedPhotoInfo]);
  const photoIdToSwapArray = useMemo(() => (photoIdToSwap ? [photoIdToSwap] : undefined), [photoIdToSwap]);
  const disabledItems = useMemo(() => (photoIdToSwap ? selectedItemsId.filter(id => id !== photoIdToSwap) : []), [photoIdToSwap, selectedItemsId]);

  return (
    <>
      <MultiPhotoSelector
        photoData={selectedPhotoInfo}
        min={minPhotos}
        max={maxPhotos}
        aspectRatio={aspectRatio}
        handleOnChange={handleOnChange}
        handlePhotoSwap={handlePhotoSwap}
        handlePhotoDelete={handlePhotoDelete}
        onDragEnd={handleDragEnd}
        onAddPhotosClick={handleOpenPickerDialog}
        isDisabledDrag={true}
        cardBgColor="white"
      />
      <PhotoUploader
        isOpenPickerDialog={isOpenPickerDialog}
        onOpenPickerDialog={onOpenPickerDialog}
        onClosePickerDialog={handleClosePickerDialog}
        containerId={eventInfo?.eventId || ''}
        eventId={eventInfo?.eventId || ''}
        isFetching={isLoadingEventMedia}
        media={media}
        showUploaderEntryPointCTA={false}
        showCount={true}
        pickerType={PICKER_TYPES.PICK_FROM_ALL_PHOTOS}
        onPhotoSelect={handlePhotoSelect}
        maxCount={photoIdToSwap ? 1 : maxPhotos}
        minCount={photoIdToSwap ? 1 : minPhotos}
        selectedItems={photoIdToSwapArray || selectedItemsId}
        disabledItems={disabledItems}
        useCase={useCase}
      />
    </>
  );
});

PhotoSelector.displayName = 'PhotoSelector';
